import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ceoApi } from "@/features/ceo/api/ceo";

const K = {
  projectGrants: ["ceo-project-grants"] as const,
  directorsProjects: ["ceo-directors-projects"] as const,
  salaryGrants: ["ceo-salary-grants"] as const,
  directorsSalary: ["ceo-directors-salary"] as const,
  scheduleAccess: ["ceo-schedule-access"] as const,
  agreementAccess: ["ceo-agreement-access"] as const,
  users: ["ceo-users"] as const,
  companies: ["ceo-companies"] as const,
  groupFinanceAccess: ["ceo-group-finance-access"] as const,
  internalCostAccess: ["ceo-internal-cost-access"] as const,
  dlcAccess: ["ceo-dlc-access"] as const,
  branches: ["ceo-branches"] as const,
};

export function useAllUsers() {
  return useQuery({ queryKey: K.users, queryFn: ceoApi.listUsers });
}

export function usePatchUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.patchUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K.users });
    },
  });
}

export function useDirectorsWithProjectGrants() {
  return useQuery({ queryKey: K.directorsProjects, queryFn: ceoApi.listDirectorsWithProjectGrants });
}

export function useCreateProjectGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.createProjectGrant,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K.directorsProjects });
      qc.invalidateQueries({ queryKey: K.projectGrants });
    },
  });
}

export function useDeleteProjectGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteProjectGrant,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K.directorsProjects });
      qc.invalidateQueries({ queryKey: K.projectGrants });
    },
  });
}

export function useDirectorsWithSalaryGrants() {
  return useQuery({ queryKey: K.directorsSalary, queryFn: ceoApi.listDirectorsWithSalaryGrants });
}

export function useCreateSalaryGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.createSalaryGrant,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K.directorsSalary });
      qc.invalidateQueries({ queryKey: K.salaryGrants });
    },
  });
}

export function useDeleteSalaryGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteSalaryGrant,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K.directorsSalary });
      qc.invalidateQueries({ queryKey: K.salaryGrants });
    },
  });
}

export function useViewersWithScheduleGrants() {
  return useQuery({ queryKey: K.scheduleAccess, queryFn: ceoApi.listViewersWithScheduleGrants });
}

export function useCreateScheduleGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.createScheduleGrant,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.scheduleAccess }); },
  });
}

export function useDeleteScheduleGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteScheduleGrant,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.scheduleAccess }); },
  });
}

export function useUsersWithAgreementGrants() {
  return useQuery({ queryKey: K.agreementAccess, queryFn: ceoApi.listUsersWithAgreementGrants });
}

export function useCreateAgreementViewGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.createAgreementViewGrant,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.agreementAccess }); },
  });
}

export function useDeleteAgreementViewGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteAgreementViewGrant,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.agreementAccess }); },
  });
}

export function useUsersWithGroupGrants() {
  return useQuery({ queryKey: K.groupFinanceAccess, queryFn: ceoApi.listUsersWithGroupGrants });
}

export function useCreateGroupFinanceGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.createGroupFinanceGrant,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.groupFinanceAccess }); },
  });
}

export function useDeleteGroupFinanceGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteGroupFinanceGrant,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.groupFinanceAccess }); },
  });
}

export function useDirectorsWithInternalCostAccess() {
  return useQuery({ queryKey: K.internalCostAccess, queryFn: ceoApi.listDirectorsWithInternalCostAccess });
}

export function useSetInternalCostAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.setInternalCostAccess,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.internalCostAccess }); },
  });
}

export function useDeleteInternalCostAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteInternalCostAccess,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.internalCostAccess }); },
  });
}

export function useUsersWithDLCAccess() {
  return useQuery({ queryKey: K.dlcAccess, queryFn: ceoApi.listUsersWithDLCAccess });
}

export function useCreateDLCAccessGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.createDLCAccessGrant,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.dlcAccess }); },
  });
}

export function useDeleteDLCAccessGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteDLCAccessGrant,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.dlcAccess }); },
  });
}

export function useCompanies() {
  return useQuery({ queryKey: K.companies, queryFn: ceoApi.listCompanies });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.createCompany,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.companies }); },
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.updateCompany,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.companies }); },
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteCompany,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.companies }); },
  });
}

export function useUploadCompanyLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.uploadCompanyLogo,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.companies }); },
  });
}

export function useDeleteCompanyLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.deleteCompanyLogo,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.companies }); },
  });
}

export function useBranches() {
  return useQuery({ queryKey: K.branches, queryFn: ceoApi.listBranches, staleTime: 0 });
}

export function useCreateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.createBranch,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.branches }); },
  });
}

export function useUpdateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ceoApi.updateBranch,
    onSuccess: () => { qc.invalidateQueries({ queryKey: K.branches }); },
  });
}
